import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Package, AlertTriangle, ClipboardList, CalendarCheck, Hammer, Wrench, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import { cn, formatCurrency } from '@/lib/utils';
import { fetchSalesEntries } from '@/lib/api/salesEntries';
import { aggregate, addDays, todayISO } from '@/lib/salesAnalytics';

const ORDER_STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  picking: 'bg-orange-100 text-orange-800',
  building: 'bg-blue-100 text-blue-800',
  software: 'bg-cyan-100 text-cyan-800',
  testing: 'bg-purple-100 text-purple-800',
  awaiting_review: 'bg-pink-100 text-pink-800',
  in_progress: 'bg-purple-100 text-purple-800',
  'In Progress': 'bg-blue-100 text-blue-800',
};

// Was a hand-written list that also named 'picking', 'in_progress' and
// 'In Progress' - none of which the CHECK constraint on assembly_orders
// permits, so they matched nothing.
import { PIPELINE_STATUSES } from '@/lib/assemblyOrderStatus';
const ACTIVE_AO_STATUSES = [...PIPELINE_STATUSES];

export function DashboardPage() {
  // Summary counts
  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['admin-dashboard-summary'],
    queryFn: async () => {
      const [paoUnits, lowStock, activeOrders, activeReservations, unitsToBuild, inProductionUnits] = await Promise.all([
        supabase.from('assembly_orders').select('quantity').in('status', ['Posted', 'Completed']),
        supabase.from('components').select('id', { count: 'exact', head: true }).eq('status', 'Active').lt('available_qty', 5),
        supabase.from('assembly_orders').select('id', { count: 'exact', head: true })
          .in('status', ACTIVE_AO_STATUSES),
        supabase.from('reservations').select('id', { count: 'exact', head: true })
          .neq('status', 'Completed').neq('status', 'Cancelled'),
        supabase.from('reservations').select('quantity, quantity_fulfilled')
          .neq('status', 'Completed').neq('status', 'Cancelled'),
        supabase.from('assembly_orders').select('quantity').in('status', ACTIVE_AO_STATUSES),
      ]);
      const totalUnitsBuilt = (paoUnits.data ?? []).reduce((sum: number, r: any) => sum + (r.quantity ?? 0), 0);
      const units = (unitsToBuild.data ?? []).reduce((sum: number, r: any) => sum + Math.max(0, (r.quantity ?? 0) - (r.quantity_fulfilled ?? 0)), 0);
      const inProduction = (inProductionUnits.data ?? []).reduce((sum: number, r: any) => sum + (r.quantity ?? 0), 0);
      return {
        totalUnitsBuilt,
        lowStock: lowStock.count ?? 0,
        activeOrders: activeOrders.count ?? 0,
        activeReservations: activeReservations.count ?? 0,
        unitsToBuild: units,
        inProduction,
      };
    },
  });

  // Sales, last 30 days. This page is admin-only (App.tsx route guard) and
  // sales_entries is admin-only at the database, so the full figures are safe
  // here — unlike build detail, which every role can reach.
  const { data: sales30d } = useQuery({
    queryKey: ['admin-dashboard-sales-30d'],
    queryFn: async () => {
      const to = todayISO();
      return aggregate(await fetchSalesEntries({ from: addDays(to, -29), to }));
    },
    staleTime: 5 * 60_000,
  });

  // Active reservations list
  const { data: reservations } = useQuery({
    queryKey: ['admin-active-reservations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('reservations')
        .select('id, reservation_code, build_sku, quantity, status, reservation_date')
        .neq('status', 'Completed').neq('status', 'Cancelled')
        .order('reservation_date', { ascending: false })
        .limit(10);
      if (error) throw error;
      return data ?? [];
    },
  });

  // Active assembly orders
  const { data: activeOrders } = useQuery({
    queryKey: ['admin-active-orders'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('assembly_orders')
        .select('id, order_number, build_sku, quantity, status, technician_id')
        .in('status', ACTIVE_AO_STATUSES)
        .order('created_at', { ascending: false })
        .limit(10);
      if (error) throw error;
      if (!data || data.length === 0) return [];

      const techIds = [...new Set(data.map((o: any) => o.technician_id).filter(Boolean))];
      const techMap: Record<string, string> = {};
      if (techIds.length > 0) {
        const { data: techs } = await supabase
          .from('technicians')
          .select('id, name, initials')
          .in('id', techIds);
        (techs ?? []).forEach((t: any) => { techMap[t.id] = `${t.name} (${t.initials})`; });
      }
      return data.map((o: any) => ({ ...o, technicianLabel: techMap[o.technician_id] || '—' }));
    },
  });

  if (summaryLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground">Overview of PC building operations</p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <Link to="/assembly-orders" className="block">
          <Card className="hover:bg-muted/50 transition-colors">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Units Built</CardTitle>
              <Package className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary?.totalUnitsBuilt ?? 0}</div>
              <p className="text-xs text-muted-foreground">From posted orders</p>
            </CardContent>
          </Card>
        </Link>

        <Link to="/components" className="block">
          <Card className="hover:bg-muted/50 transition-colors">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Low Stock</CardTitle>
              <AlertTriangle className="h-4 w-4 text-yellow-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">{summary?.lowStock ?? 0}</div>
              <p className="text-xs text-muted-foreground">Active components &lt; 5 units</p>
            </CardContent>
          </Card>
        </Link>

        <Link to="/assembly-orders" className="block">
          <Card className="hover:bg-muted/50 transition-colors">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Active Orders</CardTitle>
              <ClipboardList className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{summary?.activeOrders ?? 0}</div>
              <p className="text-xs text-muted-foreground">In progress</p>
            </CardContent>
          </Card>
        </Link>

        <Link to="/reservations" className="block">
          <Card className="hover:bg-muted/50 transition-colors">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Active Reservations</CardTitle>
              <CalendarCheck className="h-4 w-4 text-purple-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">{summary?.activeReservations ?? 0}</div>
              <p className="text-xs text-muted-foreground">Pending fulfilment</p>
            </CardContent>
          </Card>
        </Link>

        <Link to="/reservations" className="block">
          <Card className="hover:bg-muted/50 transition-colors">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Units to Build</CardTitle>
              <Hammer className="h-4 w-4 text-emerald-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-emerald-600">{summary?.unitsToBuild ?? 0}</div>
              <p className="text-xs text-muted-foreground">Across all reservations</p>
            </CardContent>
          </Card>
        </Link>

        <Link to="/assembly-orders" className="block">
          <Card className="hover:bg-muted/50 transition-colors">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">In Production</CardTitle>
              <Wrench className="h-4 w-4 text-indigo-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-indigo-600">{summary?.inProduction ?? 0}</div>
              <p className="text-xs text-muted-foreground">Units in active assembly orders</p>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Sales — last 30 days */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-green-500" />
            Sales — last 30 days
          </CardTitle>
          <Link to="/sales-report" className="text-xs text-muted-foreground hover:text-primary">Full report</Link>
        </CardHeader>
        <CardContent>
          {!sales30d ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : sales30d.entries === 0 ? (
            <p className="text-sm text-muted-foreground">
              No sales posted in the last 30 days. Import a BC export from the sales report.
            </p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Units sold</p>
                <p className="text-2xl font-bold">{sales30d.units}</p>
                {sales30d.returnUnits > 0 && (
                  <p className="text-xs text-muted-foreground">
                    net of {sales30d.returnUnits} return{sales30d.returnUnits !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Revenue</p>
                <p className="text-2xl font-bold">{formatCurrency(sales30d.revenue)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Margin</p>
                <p className={cn('text-2xl font-bold', sales30d.margin < 0 && 'text-red-600')}>
                  {formatCurrency(sales30d.margin)}
                </p>
                {sales30d.marginPct != null && (
                  <p className="text-xs text-muted-foreground">{sales30d.marginPct.toFixed(1)}%</p>
                )}
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Avg selling price</p>
                <p className="text-2xl font-bold">{sales30d.asp != null ? formatCurrency(sales30d.asp) : '—'}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Two column layout */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Active Reservations */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <CalendarCheck className="h-5 w-5 text-purple-500" />
              Active Reservations
            </CardTitle>
            <Link to="/reservations" className="text-xs text-muted-foreground hover:text-primary">View all</Link>
          </CardHeader>
          <CardContent>
            {reservations && reservations.length > 0 ? (
              <div className="space-y-2">
                {reservations.map((r: any) => (
                  <Link
                    key={r.id}
                    to={`/reservations/${r.id}`}
                    className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium">{r.reservation_code}</p>
                      <p className="text-xs text-muted-foreground font-mono">{r.build_sku} × {r.quantity}</p>
                    </div>
                    <Badge variant="outline" className="text-xs ml-3 shrink-0">{r.status}</Badge>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-32 text-muted-foreground">
                No active reservations
              </div>
            )}
          </CardContent>
        </Card>

        {/* Active Assembly Orders */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-blue-500" />
              Active Assembly Orders
            </CardTitle>
            <Link to="/assembly-orders" className="text-xs text-muted-foreground hover:text-primary">View all</Link>
          </CardHeader>
          <CardContent className="p-0">
            {activeOrders && activeOrders.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-3 font-medium">Order #</th>
                      <th className="text-left p-3 font-medium">Build SKU</th>
                      <th className="text-left p-3 font-medium">Technician</th>
                      <th className="text-right p-3 font-medium">Qty</th>
                      <th className="text-center p-3 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeOrders.map((o: any) => (
                      <tr key={o.id} className="border-b hover:bg-muted/50 transition-colors">
                        <td className="p-3 font-mono text-xs font-medium">{o.order_number}</td>
                        <td className="p-3 font-mono text-xs">{o.build_sku || '—'}</td>
                        <td className="p-3 text-sm">{o.technicianLabel}</td>
                        <td className="p-3 text-right font-medium">{o.quantity ?? '—'}</td>
                        <td className="p-3 text-center">
                          <Badge className={cn('text-xs', ORDER_STATUS_COLORS[o.status] || 'bg-gray-100 text-gray-700')}>
                            {o.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex items-center justify-center h-32 text-muted-foreground">
                No active assembly orders
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
